import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService } from '../services/authService';
import { User, LoginCredentials, AuthState } from '../types';

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => void;
  isAdmin: boolean;
  isHR: boolean;
  isEmployee: boolean;
  isAdminHead: boolean;
  canApproveLeave: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: true,
  });

  // Initialize auth state from localStorage
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      const userStr = localStorage.getItem('user');
      
      if (token && userStr) {
        try {
          const user = JSON.parse(userStr) as User;
          setState({
            user,
            token,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch {
          // Invalid stored data, clear it
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setState({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      } else {
        setState({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
        });
      }
    };
    
    initAuth();
  }, []);

  const login = async (credentials: LoginCredentials) => {
    const response = await authService.login(credentials);
    
    localStorage.setItem('token', response.token);
    localStorage.setItem('user', JSON.stringify(response.user));
    
    setState({
      user: response.user,
      token: response.token,
      isAuthenticated: true,
      isLoading: false,
    });
  };

  const logout = () => {
    authService.logout();
    setState({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
    });
  };

  const isAdmin = state.user?.role === 'ADMIN';
  // Admin Head emails get full admin access
  /** Admin emails: same list as backend – always grant admin/HR-level access. Update when admins change. */
  const ADMIN_HEAD_EMAILS = [
    'ronak.soni@southasiaconsultancy.com',
    'dipali.patt@southasiaconsultancy.com',
    'hr@example.com',
  ];
  const isAdminHead = state.user?.email ? ADMIN_HEAD_EMAILS.includes(state.user.email.toLowerCase()) : false;
  const isHR = state.user?.role === 'HR' || state.user?.role === 'ADMIN' || isAdminHead;
  const isEmployee = state.user?.role === 'EMPLOYEE' && !isAdminHead;
  // Leave approval: Admin/HR or employee's manager
  const canApproveLeave = isHR || state.user?.role === 'MANAGER';

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        isAdmin,
        isHR,
        isEmployee,
        isAdminHead,
        canApproveLeave,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
